<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  type ProjectReviewStatus,
  type ExpertReviewSchema,
  useApi,
  UiDialog,
  UiText,
  UiButton,
  UiTabs,
  UiTabsItem,
  useToaster,
} from "@shopexperts/shared";
import { useProjectDetail } from "@/v2Projects/composables/useProjectDetail";
import { useProjectMessages } from "@/v2Projects/composables/useProjectMessages";
import { useChatActions } from "@/v2Projects/composables/useChatActions";
import { useProjectQuotes } from "@/v2Projects/composables/useProjectQuotes";
import { useProjectStatus } from "@/v2Projects/composables/useProjectStatus";
import { getConvexAuthInfo } from "@/v2Projects/convexClient";
import { getStatusConfig } from "@/v2Projects/utils/display";
import StartDirectMessageButton from "@/v2Projects/components/StartDirectMessageButton.vue";
import ChatFeed from "@/v2Projects/components/ChatFeed.vue";
import ChatComposer from "@/v2Projects/components/ChatComposer.vue";
import ChatSidebar from "@/v2Projects/components/ChatSidebar.vue";
import type { z } from "zod";
import EditReviewForm from "@/app/components/reviews/EditReviewForm.vue";
import RespondToReviewModal from "@/app/components/reviews/RespondToReviewModal.vue";
import ShareReviewDialog from "@/app/components/reviews/ShareReviewDialog.vue";
import ProjectReviewCard from "@/v2Projects/components/ProjectReviewCard.vue";
import ProjectReviewPanel from "@/v2Projects/components/ProjectReviewPanel.vue";
import ProjectReviewRequestsCard from "@/v2Projects/components/ProjectReviewRequestsCard.vue";
import ProjectInfoTab from "@/v2Projects/components/ProjectInfoTab.vue";
import ProjectQuoteAside from "@/v2Projects/components/ProjectQuoteAside.vue";
import ProjectQuotesTab from "@/v2Projects/components/ProjectQuotesTab.vue";
import ProjectStatusHistoryTab from "@/v2Projects/components/ProjectStatusHistoryTab.vue";
import ProjectActionBar from "@/v2Projects/components/ProjectActionBar.vue";
import ProjectStatusConfirmDialog, {
  type ProjectStatusConfirmPayload,
  type ProjectStatusDialogAction,
} from "@/v2Projects/components/ProjectStatusConfirmDialog.vue";
import { reportProjectUserError, type UserErrorMessage } from "@/v2Projects/userErrors";
import { shouldHideHelpScoutBeacon } from "@/app/utils/helpScoutBeaconVisibility";

const reviewStatus = ref<ProjectReviewStatus>();
const editReviewOpen = ref(false);
const replyReviewOpen = ref(false);
const router = useRouter();
const route = useRoute();
const {
  project,
  attachments,
  participants,
  projectId,
  side,
  isLoading,
  error,
  refetch,
} = useProjectDetail();

const {
  messages,
  threadId,
  isReady: areMessagesReady,
  isLoadingMore,
  loadError: messagesLoadError,
  hasMore,
  loadMore,
  refresh: refreshMessages,
} =
  useProjectMessages(projectId);
const projectLoadError = ref<UserErrorMessage | null>(null);

watch(error, (nextError) => {
  projectLoadError.value = nextError
    ? reportProjectUserError("loadProject", nextError, {
        projectId: projectId.value,
        side: side.value,
      })
    : null;
}, { immediate: true });

const { sendMessage, sendNote, uploadAttachments } = useChatActions(
  projectId,
  side,
);

const {
  quotes,
  isLoading: isQuotesLoading,
  createQuote,
  cancelQuote,
  rejectQuote,
  viewQuote,
  updateQuotePaymentStatus,
  quoteCurrencies,
  defaultQuoteCurrency,
} = useProjectQuotes(projectId, side);

const activeTab = ref("chatroom");
const isDescriptionExpanded = ref(false);
const isQuoteAsideOpen = ref(false);

const deepLinkableTabs = new Set(["info", "chatroom", "quotes", "status_history"]);

function getRequestedTab(): string | null {
  const rawTab = route.query.tab;
  const tab = Array.isArray(rawTab) ? rawTab[0] : rawTab;
  return typeof tab === "string" && deepLinkableTabs.has(tab) ? tab : null;
}

type HelpScoutBeaconApi = (method: string, options?: unknown, data?: unknown) => void;

function setHelpScoutBeaconHidden(hidden: boolean) {
  if (typeof window === "undefined") return;

  const beacon = (window as Window & { Beacon?: HelpScoutBeaconApi }).Beacon;
  if (!beacon) return;

  if (hidden) {
    beacon("close");
    beacon("config", { display: { style: "manual" } });
    return;
  }

  beacon("config", { display: { style: "icon" } });
}

const focusMessageId = computed(() => {
  const rawMessageId = route.query.messageId;
  if (typeof rawMessageId === "string" && rawMessageId.length > 0) {
    return rawMessageId;
  }
  if (Array.isArray(rawMessageId)) {
    const firstMessageId = rawMessageId.find(
      (value): value is string => typeof value === "string" && value.length > 0,
    );
    return firstMessageId ?? null;
  }
  return null;
});

const shouldOpenProposalFromLink = computed(() => {
  const rawValue = route.query.openProposal;
  return rawValue === "1" || rawValue === "true";
});

const openProposalMessageId = computed(() =>
  shouldOpenProposalFromLink.value ? focusMessageId.value : null,
);

const authInfo = computed(() => getConvexAuthInfo());
const currentUserId = computed(() => authInfo.value?.userId ?? 0);
const currentOrgId = computed(() => authInfo.value?.orgId ?? 0);
const directRecipient = computed(() => participants.value?.participants.find(p =>
  p.is_active && p.participant_role === "owner" && p.participant_group === (side.value === "client" ? "expert" : "client"),
)?.org_id);
const isAdmin = computed(() => authInfo.value?.isAdmin ?? false);

const {
  allowedTransitions,
  updateProjectStatus,
} = useProjectStatus(
  projectId,
  side,
  currentOrgId,
  computed(() => project.value?.project_status ?? null),
  quotes,
);

const status = computed(() =>
  project.value ? getStatusConfig(project.value.project_status, project.value.project_type) : null,
);

const expertRole = computed(() => {
  const expert = project.value?.expert;
  if (!expert) return null;
  if (expert.role) return expert.role;
  if (!expert.type) return null;
  return expert.type
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
});

const backRouteName = computed(() =>
  side.value === "expert" ? "expert-projects" : "client-projects",
);
const backLabel = computed(() =>
  side.value === "expert" ? "Back to Leads & Projects" : "Back to Projects",
);

function goBack() {
  router.push({ name: backRouteName.value });
}

function openQuoteAside() {
  isQuoteAsideOpen.value = true;
}

watch(
  focusMessageId,
  (messageId) => {
    if (messageId) {
      activeTab.value = "chatroom";
    }
  },
  { immediate: true },
);

watch(
  () => route.query.tab,
  () => {
    const requestedTab = getRequestedTab();
    if (requestedTab) {
      activeTab.value = requestedTab;
    }
  },
  { immediate: true },
);

setHelpScoutBeaconHidden(true);

onBeforeUnmount(() => {
  if (shouldHideHelpScoutBeacon(window.location.pathname)) return;

  setHelpScoutBeaconHidden(false);
});

async function handleSendMessage(input: {
  threadId: string;
  content: string;
  attachments: import("@/v2Projects/types").ChatMessageAttachment[];
}) {
  return sendMessage.mutateAsync(input);
}

async function handleSendNote(input: {
  threadId: string;
  noteKind: import("@/v2Projects/types").NoteKind;
  content: string;
  attachments: import("@/v2Projects/types").ChatMessageAttachment[];
}) {
  return sendNote.mutateAsync(input);
}

async function handleUpload(input: { files: File[]; actingOrgId: number }) {
  return (await uploadAttachments.mutateAsync(input)) as import("@/v2Projects/types").ProjectMediaResource[];
}

async function handleRejectQuoteFromChat(quoteId: number) {
  await rejectQuote.mutateAsync({
    quoteId,
    actingOrgId: currentOrgId.value,
  });
}

async function handleViewQuoteFromChat(quoteId: number) {
  await viewQuote.mutateAsync({
    quoteId,
    actingOrgId: currentOrgId.value,
  });
}

function handleMessageFocusComplete(_payload: {
  messageId: string;
  found: boolean;
}) {
  // Keep the deep-link query in place for debugging and repeatable focus behavior.
}

const toaster = useToaster();
const api = useApi();
const shareReview = ref<z.infer<typeof ExpertReviewSchema> | null>(null);
const shareReviewOpen = ref(false);
const loadingShare = ref(false);

async function openReviewShare(reviewId: number) {
  if (side.value !== 'expert' || loadingShare.value) return;
  loadingShare.value = true;
  const requestedProject = projectId.value;
  const requestedUser = currentUserId.value;
  try {
    // Use the existing expert review representation to preserve anonymous
    // reviewer details and the verified badge in the shared artwork.
    let cursor: string | undefined;
    const visited = new Set<string>();
    do {
      const page = await api.expert.reviewsV2.getReviews({ query: { limit: 100, cursor } });
      if (projectId.value !== requestedProject || currentUserId.value !== requestedUser) return;
      const found = page.data.find(review => review.id === reviewId);
      if (found) {
        if (found.status !== 'published') throw new Error('Only published reviews can be shared.');
        shareReview.value = found;
        shareReviewOpen.value = true;
        return;
      }
      cursor = page.pagination.has_next ? page.pagination.cursor ?? undefined : undefined;
      if (cursor && visited.has(cursor)) break;
      if (cursor) visited.add(cursor);
    } while (cursor);
    throw new Error('This review is no longer available to share.');
  } catch (error) {
    toaster.create({ title: 'Could not open review sharing', description: error instanceof Error ? error.message : 'Please try again.', type: 'error' });
  } finally {
    loadingShare.value = false;
  }
}
watch([projectId, currentUserId], () => { shareReviewOpen.value = false; shareReview.value = null; editReviewOpen.value = false; replyReviewOpen.value = false; });
const sidebarDialogOpen = ref(false);
const sidebarDialogAction = ref<ProjectStatusDialogAction | null>(null);

const sidebarActionMap: Record<string, ProjectStatusDialogAction> = {
  set_in_progress: {
    targetStatus: "in_progress",
    label: "Set to In Progress",
    description: "Manually move this project into active delivery. Normally this happens automatically when a quote is paid.",
    confirmLabel: "Set to In Progress",
  },
  submit_work: {
    targetStatus: "submitted",
    label: "Submit Work",
    description: "Mark this project as delivered so the client can review and complete it.",
    confirmLabel: "Submit Work",
    offersAutoReviewRequests: true,
  },
  complete_project: {
    targetStatus: "completed",
    label: "Complete Project",
    description: "Confirm delivery is approved and close the project as completed.",
    confirmLabel: "Complete Project",
  },
};

function handleSidebarStatusAction(actionKey: string) {
  const action = sidebarActionMap[actionKey];
  if (!action) return;
  sidebarDialogAction.value = action;
  sidebarDialogOpen.value = true;
}

async function confirmSidebarAction({ autoReviewRequestsEnabled }: ProjectStatusConfirmPayload) {
  const action = sidebarDialogAction.value;
  if (!action) return;

  try {
    await updateProjectStatus.mutateAsync({ status: action.targetStatus, autoReviewRequestsEnabled });
    toaster.create({ title: `${action.label} successful`, type: "success" });
    sidebarDialogOpen.value = false;
    sidebarDialogAction.value = null;
  } catch (error) {
    const message = reportProjectUserError("updateProjectStatus", error, {
      projectId: projectId.value,
      side: side.value,
      extra: { targetStatus: action.targetStatus },
    });
    toaster.create({ title: message.title, description: message.description, type: "error" });
  }
}
</script>

<template>
  <main class="flex-1 min-h-0 h-full w-full overflow-hidden flex flex-col bg-secondary">
    <!-- Loading state -->
    <div v-if="isLoading" class="flex-1 flex flex-col items-center justify-center gap-3">
      <div class="w-7 h-7 border-2 border-gray-800 border-t-transparent rounded-full animate-spin" />
      <UiText as="p" variant="p-small" class="text-gray-500">
        Loading project...
      </UiText>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="flex-1 flex flex-col items-center justify-center gap-3">
      <div class="bg-white border border-gray-200 rounded-xl p-6 max-w-md text-center space-y-3">
        <UiText as="h3" variant="p" class="font-semibold text-gray-900">
          The project workspace could not be opened
        </UiText>
        <UiText as="p" variant="p-small" class="text-gray-500">
          {{ projectLoadError?.description }}
        </UiText>
        <UiButton variant="outline" label="Try Again" @click="refetch" />
      </div>
    </div>

    <!-- Project loaded -->
    <template v-else-if="project">
      <!-- Header: Title + collapsible description -->
      <div class="px-5 pt-4 pb-0">
        <div class="flex items-start gap-3">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2.5 max-sm:gap-2">
              <button
                class="group inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border border-se-gray-300 bg-white text-se-gray-700 transition-all hover:border-se-gray-400 hover:bg-se-gray-50 hover:text-se-gray-900 shrink-0 max-sm:hidden"
                type="button"
                :aria-label="backLabel"
                @click="goBack"
              >
                <svg
                  class="h-4 w-4 transition-transform group-hover:-translate-x-0.5"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                >
                  <path d="M10 4l-4 4 4 4" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>
              <button
                v-if="project.description"
                type="button"
                class="inline-flex min-w-0 cursor-pointer items-center gap-1.5 rounded-md text-left text-gray-900 transition-colors hover:text-gray-700"
                @click="isDescriptionExpanded = !isDescriptionExpanded"
              >
                <UiText as="h1" variant="h5" class="font-semibold text-gray-900 truncate max-sm:whitespace-normal max-sm:overflow-visible max-sm:text-clip">
                  {{ project.title }}
                </UiText>
                <svg
                  class="h-5 w-5 shrink-0 text-gray-400 transition-transform duration-200 max-sm:hidden"
                  :class="isDescriptionExpanded ? 'rotate-180' : ''"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
              <UiText v-else as="h1" variant="h5" class="font-semibold text-gray-900 truncate max-sm:whitespace-normal max-sm:overflow-visible max-sm:text-clip">
                {{ project.title }}
              </UiText>
              <span
                v-if="status"
                :class="status.badgeClass"
                class="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold leading-4 tracking-wide shrink-0"
              >
                {{ status.label }}
              </span>
              <div class="ml-auto flex flex-wrap items-center justify-end gap-2">
                <StartDirectMessageButton v-if="directRecipient && side !== 'admin'" :recipient-org-id="directRecipient" :side="side" />
                <ProjectActionBar
                  :project="project"
                  :side="side"
                  :quotes="quotes"
                  :allowed-transitions="allowedTransitions"
                  :update-project-status="updateProjectStatus"
                  @create-quote="openQuoteAside"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Collapsible description -->
        <div
          class="overflow-hidden transition-all duration-200 ease-in-out"
          :style="{ maxHeight: isDescriptionExpanded ? '300px' : '0px' }"
        >
          <div class="ml-10.5 pt-2 pb-3 max-sm:ml-0 max-sm:pt-3">
            <UiText
              as="p"
              variant="p-small"
              class="text-gray-600 leading-relaxed whitespace-pre-wrap max-w-2xl"
            >
              {{ project.description }}
            </UiText>
          </div>
        </div>
      </div>

      <ProjectReviewPanel :key="`${currentUserId}:${project.id}`" :project="project" @status="reviewStatus = $event" />

      <!-- Tabs -->
      <div class="flex-1 flex flex-col min-h-0 project-detail-tabs">
        <UiTabs v-model="activeTab" default-value="chatroom">
          <UiTabsItem value="info" label="Info">
            <div class="flex-1 overflow-y-auto px-4">
              <div class="space-y-4 px-1 py-5">
                <div class="lg:hidden">
                  <ChatSidebar
                    :project="project"
                    :side="side"
                    bordered
                    @open-quote-aside="openQuoteAside"
                    @status-action="handleSidebarStatusAction"
                  />
                  <ProjectReviewCard v-if="reviewStatus?.project_id === project.id && reviewStatus.has_review" :status="reviewStatus" :side="side" @edit="editReviewOpen = true" @reply="replyReviewOpen = true" :can-share="side === 'expert'" :is-loading-share="loadingShare" class="mt-4" @share="openReviewShare" />
                  <ProjectReviewRequestsCard v-if="side === 'expert' && reviewStatus?.project_id === project.id && !reviewStatus.has_review" :project-id="project.id" :acting-org-id="currentOrgId" :project-status="project.project_status" :status="reviewStatus" class="mt-4" />
                </div>
                <ProjectInfoTab :project="project" :attachments="attachments" :side="side" />
              </div>
            </div>
          </UiTabsItem>

          <UiTabsItem value="chatroom" label="Chatroom">
            <div class="flex-1 min-h-0 overflow-hidden p-4 max-sm:p-0">
              <div class="flex h-full min-h-0 items-start gap-4 overflow-hidden">
              <!-- Chat card (left) -->
                <div
                  class="relative flex-1 min-w-0 h-full min-h-0 flex flex-col bg-white border border-gray-200 rounded-xl overflow-hidden max-sm:border-0 max-sm:rounded-none"
                >
                  <ChatFeed
                    :messages="messages"
                    :participants-display="participants"
                    :current-user-id="currentUserId"
                    :current-org-id="currentOrgId"
                    :side="side"
                    :is-ready="areMessagesReady"
                    :is-loading-more="isLoadingMore"
                    :has-more="hasMore"
                    :focus-message-id="focusMessageId"
                    :open-proposal-message-id="openProposalMessageId"
                    :quotes="quotes"
                    :expert-role="expertRole"
                    :reject-quote="side === 'client' ? handleRejectQuoteFromChat : undefined"
                    :view-quote="side === 'client' ? handleViewQuoteFromChat : undefined"
                    class="flex-1 min-h-0"
                    @load-more="loadMore"
                    @focus-complete="handleMessageFocusComplete"
                  />
                  <div
                    v-if="messagesLoadError"
                    class="border-t border-amber-200 bg-amber-50 px-4 py-3"
                  >
                    <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <UiText as="p" variant="p-small" class="text-amber-900">
                        {{ messagesLoadError }}
                      </UiText>
                      <UiButton
                        variant="outline"
                        size="sm"
                        label="Retry messages"
                        @click="refreshMessages"
                      />
                    </div>
                  </div>
                  <ChatComposer
                    :project-id="projectId"
                    :side="side"
                    :is-admin="isAdmin"
                    :acting-org-id="currentOrgId"
                    :thread-id="threadId ?? null"
                    :send-message="handleSendMessage"
                    :send-note="handleSendNote"
                    :upload-attachments="handleUpload"
                  />
                </div>

              <!-- Details sidebar (right) -->
                <div
                  class="hidden lg:block w-72 xl:w-80 shrink-0 self-start max-h-full overflow-y-auto"
                >
                  <ChatSidebar
                    :project="project"
                    :side="side"
                    @open-quote-aside="openQuoteAside"
                    @status-action="handleSidebarStatusAction"
                  />
                  <ProjectReviewCard v-if="reviewStatus?.project_id === project.id && reviewStatus.has_review" :status="reviewStatus" :side="side" @edit="editReviewOpen = true" @reply="replyReviewOpen = true" :can-share="side === 'expert'" :is-loading-share="loadingShare" class="mt-4" @share="openReviewShare" />
                  <ProjectReviewRequestsCard v-if="side === 'expert' && reviewStatus?.project_id === project.id && !reviewStatus.has_review" :project-id="project.id" :acting-org-id="currentOrgId" :project-status="project.project_status" :status="reviewStatus" class="mt-4" />
                </div>
              </div>
            </div>
          </UiTabsItem>

          <UiTabsItem value="quotes" label="Quotes" :count="quotes.length">
            <div class="flex-1 min-h-0 overflow-y-auto px-4">
              <ProjectQuotesTab
                :quotes="quotes"
                :side="side"
                :acting-org-id="currentOrgId"
                :is-loading="isQuotesLoading"
                :cancel-quote="cancelQuote"
                :reject-quote="rejectQuote"
                :update-quote-payment-status="updateQuotePaymentStatus"
                @create-quote="openQuoteAside"
              />
            </div>
          </UiTabsItem>

          <UiTabsItem value="status_history" label="Status History">
            <ProjectStatusHistoryTab />
          </UiTabsItem>
        </UiTabs>
      </div>

      <ProjectQuoteAside
        v-if="side === 'expert'"
        v-model="isQuoteAsideOpen"
        :acting-org-id="currentOrgId"
        :create-quote="createQuote"
        :currency-options="quoteCurrencies"
        :default-currency="defaultQuoteCurrency"
      />
    </template>

    <!-- Not found -->
    <div v-else class="flex-1 flex flex-col items-center justify-center gap-3">
      <UiText as="p" variant="p" class="text-gray-500">Project not found</UiText>
      <UiButton variant="ghost" :label="backLabel" @click="goBack" />
    </div>

    <UiDialog v-model="editReviewOpen" title="Edit your review" size="xl">
      <EditReviewForm v-if="editReviewOpen && reviewStatus?.review && reviewStatus.can_edit_review && project"
        :review-id="reviewStatus.review.id" :show-close-button="false" :initial-values="reviewStatus.review"
        :expert="{ name: project.expert?.name || 'Your expert', type: project.expert?.type === 'freelancer' ? 'freelancer' : 'agency', slug: '', profile_picture: project.expert?.avatar, role: project.expert?.role }"
        @success="editReviewOpen = false" @cancel="editReviewOpen = false" />
    </UiDialog>
    <RespondToReviewModal v-if="replyReviewOpen && reviewStatus?.review && reviewStatus.can_reply"
      :key="reviewStatus.review.id" v-model:open="replyReviewOpen" :review-id="reviewStatus.review.id"
      :existing-response="reviewStatus.review.review_response?.response_text" />

    <ShareReviewDialog v-if="shareReviewOpen && shareReview" :key="shareReview.id" v-model:open="shareReviewOpen" :review="shareReview" />

    <ProjectStatusConfirmDialog
      v-if="project"
      v-model="sidebarDialogOpen"
      :action="sidebarDialogAction"
      :current-status="project.project_status"
      :is-loading="updateProjectStatus.isPending.value"
      :auto-review-requests-enabled="project.auto_review_requests_enabled"
      @confirm="confirmSidebarAction"
    />
  </main>
</template>

<style scoped>
.project-detail-tabs :deep([data-scope="tabs"][data-part="root"]) {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.project-detail-tabs :deep([data-scope="tabs"][data-part="root"] > .border-b) {
  padding-left: 1.25rem;
  padding-right: 1.25rem;
}

.project-detail-tabs :deep([data-scope="tabs"][data-part="root"] > div:last-child) {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-top: 0;
}

.project-detail-tabs :deep([data-scope="tabs"][data-part="content"]) {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.project-detail-tabs :deep([data-scope="tabs"][data-part="content"][hidden]) {
  display: none;
}
</style>
